import { useQuery } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"
import { api } from "@/lib/api-client"

interface NeighborsResponse {
  data: {
    prev_id: string | null
    next_id: string | null
    position: number | null
    total: number
  }
}

/**
 * Maps entity types to their API path segments.
 */
const ENTITY_API_PATHS: Record<string, string> = {
  companies: "companies",
  contacts: "contacts",
  deals: "deals",
  tasks: "tasks",
  service_tickets: "service_tickets",
}

/**
 * Param keys that should be forwarded from the URL to the neighbors API.
 * These match the filter/sort params used by list pages.
 */
const FORWARDED_PARAMS = [
  "sort",
  "search",
  "status",
  "status_id",
  "priority",
  "phase_id",
  "owner_id",
  "assignee_id",
  "assigned_to_id",
  "industry_id",
  "acquisition_source_id",
  "city",
  "department",
  "company_id",
  "deal_type_id",
  "ticket_type_id",
  "value_min",
  "value_max",
  "expected_close_from",
  "expected_close_to",
  "created_at_from",
  "created_at_to",
  "due_date_from",
  "due_date_to",
]

interface UseRecordNavigationOptions {
  entityType: string
  id: string | undefined
}

export function useRecordNavigation({ entityType, id }: UseRecordNavigationOptions) {
  const [searchParams] = useSearchParams()

  const apiPath = ENTITY_API_PATHS[entityType]

  // Build query string from forwarded URL params
  const forwardedParams = new URLSearchParams()
  for (const key of FORWARDED_PARAMS) {
    const value = searchParams.get(key)
    if (value) {
      forwardedParams.set(key, value)
    }
  }
  const qs = forwardedParams.toString()

  const { data, isLoading } = useQuery({
    queryKey: ["record-navigation", entityType, id, qs],
    queryFn: () =>
      api.get<NeighborsResponse>(
        `/api/v1/${apiPath}/${id}/neighbors${qs ? `?${qs}` : ""}`
      ),
    enabled: !!id && !!apiPath,
    staleTime: 30_000, // Cache for 30s to avoid re-fetching on every render
  })

  return {
    prevId: data?.data?.prev_id ?? null,
    nextId: data?.data?.next_id ?? null,
    position: data?.data?.position ?? null,
    total: data?.data?.total ?? null,
    isLoading,
  }
}
